// Internationalization (i18n) module for STARCODE: German (DE) and English (EN)

export const translations = {
  de: {
    langLabel: "DE",
    startTitle: "STARCODE",
    startSubtitle: "GPU-Partikel-Engine",
    visitorLabel: "Besucher",
    startBuildDate: "RELEASE :: 16.08.2026",
    startButton: "Start (Audio & Bewegung aktivieren)",
    startMicHint: "Erlaube Mikrofon-Zugriff für Audio-Reaktivität (optional).",
    startShowHint: "Für Live-Shows: Syphon Popup verwenden, um den sauberen Ausgabe-Frame (Document Picture-in-Picture) ohne Browser-URL-Leiste zu öffnen.",
    creatorLink: "studiopesc.com",
    aboutButton: "Info & Kontakt",
    hudAboutBtn: "ℹ Info",
    aboutModalTitle: "Über STARCODE & STUDIO PESC",
    tabAbout: "Über das Projekt",
    tabGuide: "Tutorial & Resolume VJ",
    tabContact: "Kontakt & Creator",
    aboutText1: "STARCODE ist ein hochperformantes, GPU-beschleunigtes 3D-Partikelsystem und Live-Visual-Instrument, entwickelt für audiovisuelle Live-Performances, VJ-Sets, Theater- und immersive Medieninstallationen.",
    aboutText2: "Die gesamte Physik, Strömungsfelder, Gravitationsmodelle und astronomischen Gebilde (Schwarzes Loch, Neutronenstern, Sonne, Galaxien) werden in Echtzeit parallel auf der Grafikkarte (GPGPU Shaders) mit 120 FPS simuliert.",
    aboutFeature1Title: "Echtzeit-Audioreaktivität",
    aboutFeature1Desc: "5-Band FFT Spektralanalyse, Beat-Detection & Transientenerkennung steuern Kräfte, Farben und Geometrien live zur Musik.",
    aboutFeature2Title: "Live VJ & Resolume Syphon Workflow",
    aboutFeature2Desc: "Rahmenloses 1-Klick-Ausgabefenster, BroadcastChannel-Regie-Synchronisation und WebGL Background-Keepalive ohne Stillframes.",
    aboutFeature3Title: "Cineastische Optik",
    aboutFeature3Desc: "Autofokus, bidirektionales Bokeh / Depth of Field, Unschärfe-Invertierung und HDR-Bloom.",

    // Tutorial & Guide Content (DE)
    guideNavTitle: "1. Schnellstart & 3D-Kamerasteuerung",
    guideNavDesc: "Die Kamera kann jederzeit intuitiv im 3D-Raum bewegt werden:",
    guideNavOrbit: "Normaler Orbit:",
    guideNavOrbitDesc: "Linksklick halten und ziehen dreht die Kamera 360° um das Zentrum.",
    guideNavPan: "Kamera-Pan [1]:",
    guideNavPanDesc: "Taste <code>1</code> gedrückt halten + Maus ziehen verschiebt die Kamera parallel auf der Bildebene (X/Y).",
    guideNavZoom: "Stufenlos Zoomen [2]:",
    guideNavZoomDesc: "Taste <code>2</code> gedrückt halten + Maus auf/ab ziehen oder Mausrad/Trackpad scrollen.",
    guideNavSpin: "Freie 3D-Drehung [3]:",
    guideNavSpinDesc: "Taste <code>3</code> gedrückt halten + Maus ziehen rotiert die Kamera frei um alle räumlichen Achsen ohne Pol-Begrenzung.",
    guideNavReset: "Swarm finden / Reset:",
    guideNavResetDesc: "Button <code>🎯 Swarm finden</code> oder Taste <code>R</code> setzt Blickwinkel und Partikelfelder sofort zurück.",

    guideVJTitle: "2. Live-VJ & Resolume Arena / Syphon / OBS Workflow",
    guideVJDesc: "STARCODE wurde speziell für den professionellen Live-Bühneneinsatz mit zwei Fenstern (Regie & Visual-Ausgabe) konzipiert:",
    guideVJStep1Title: "Schritt 1: Dual-Window Setup (Regie & Output)",
    guideVJStep1Desc: "Öffne das rahmenlose Ausgabefenster mit dem Button <code>⧉ Ausgabe-Fenster (Syphon)</code> oder hänge <code>?role=output</code> an die URL an. Dieses Fenster zeigt das reine, saubere Visual ohne Buttons und ohne Mauszeiger. Auf deinem Kontrollmonitor behältst du das Hauptfenster (oder <code>?role=control</code>) mit allen Reglern. Beide Fenster synchronisieren sich verzögerungsfrei per <code>BroadcastChannel</code>.",
    guideVJStep2Title: "Schritt 2: In Resolume Arena / OBS einbinden",
    guideVJStep2Desc: "In Resolume Arena unter <b>Sources → Screen Capture</b> (oder Syphon / NDI) einfach das Fenster <b>'STARCODE — Output'</b> auswählen. STARCODE besitzt eine integrierte <b>MediaStream Keepalive Pipeline</b>, sodass die WebGL-Engine auch im Hintergrund bei aktivem Resolume dauerhaft mit vollen 60–120 FPS ohne Einfrieren rendert.",
    guideVJStep3Title: "Schritt 3: Audio-Routing zur Musik",
    guideVJStep3Desc: "Stelle die Audioquelle auf dein DJ-Mischpult, Audio-Interface oder ein virtuelles Kabel (z.B. BlackHole / Loopback). Die 5-Band FFT Spektralanalyse steuert Wellen, Blitze und Partikelkräfte live zur Musik.",

    guideFormzTitle: "3. Kosmische Formen (FORMZ) & 3D-Morph",
    guideFormzDesc: "Über die Buttons in der Kategorie <b>FORMZ</b> lassen sich astrophysikalische Großstrukturen live transformieren:",
    guideBlackHoleTitle: "🌀 Schwarzes Loch (Gargantua):",
    guideBlackHoleDesc: "Mächtiger gravitativer Ereignishorizont mit spiralförmig einströmender Akkretionsscheibe.",
    guideNeutronTitle: "⚡ Neutronenstern (Pulsar):",
    guideNeutronDesc: "Hyperdichter rotierender Magnetar-Kern mit hochenergetischen relativistischen Pulsar-Jets.",
    guideSunTitle: "☀️ SUN:",
    guideSunDesc: "Glühender Fusions-Plasmakern mit dynamischer Korona und eruptiven Protuberanzen.",
    guideMorphTitle: "3D-Morphing & Eigene OBJ-Dateien:",
    guideMorphDesc: "Mit <code>STARCODE-Morph</code> (Cmd/Ctrl+Shift+M) assembliert sich der gesamte Schwarm zum 3D-Schriftzug. Über <code>📂 OBJ laden</code> kannst du jedes eigene 3D-Modell (.obj) laden und als Partikel-Skulptur darstellen.",

    guideOpticsTitle: "4. Cineastische Optik, Farbrad, Presets & MIDI",
    guideOpticsDesc: "<b>Autofokus & Bokeh:</b> <code>AF</code> folgt der Kameraentfernung. Mit dem Schärfentiefe-Regler und <code>Weit-Bokeh</code> ⇄ <code>Nah-Bokeh</code> entstehen cinematische Bokeh-Scheiben.<br><b>Farbrad:</b> Ziehe im <code>Farbton</code>-Regler für 360° Farbton-Scrubbing.<br><b>MIDI-Learn:</b> Klicke in der Zeile <code>MIDI-Learn</code> auf einen Parameter und bewege einen Drehregler oder Fader an deinem Hardware-MIDI-Controller.<br><b>Presets:</b> Speichere mit <code>💾 Speichern</code> deine eigenen Visual-Setups dauerhaft im Browser.",

    creatorTitle: "Creator & Konzept",
    creatorName: "Peter Schönhardt",
    creatorRole: "Designer, Creative Technologist & Visual Artist",
    creatorCompany: "STUDIO PESC",
    creatorWeb: "studiopesc.com",
    creatorEmail: "peter@studiopesc.com",
    creatorLocation: "Wien / Österreich & international",
    creatorBio: "Spezialisiert auf generative Kunst, Brand Design, interaktive 3D-Erlebnisse, Motion Design und immersive Live-Visuals.",
    closeModal: "Schließen",

    // HUD Top
    fps: "fps",
    particles: "Partikel",
    audioMic: "Audio: Mikrofon",
    audioSynth: "Audio: synthetisch",
    regieWindow: "Regie-Fenster",
    midiConnected: "MIDI: verbunden",
    autoMixPrefix: "Auto-Mix: ",
    liveBadge: "● LIVE",
    resizeGripTitle: "Leiste nach oben/unten ziehen oder doppelklicken zum Minimieren",
    exitRegieBtn: "‹ Normalmodus (Visual & Steuerung hier anzeigen)",
    exitRegieTitle: "Klicken, um den Regie-Modus zu verlassen und die Partikel direkt in diesem Fenster anzuzeigen",

    // Navigation helper
    navTitle: "Kamera:",
    navPan: "<kbd>1</kbd> ✥ Pan",
    navZoom: "<kbd>2</kbd> ⇕ Zoom",
    navSpin: "<kbd>3</kbd> 🔄 3D-Spin",
    navPanTip: "Taste [1] gedrückt halten + Maus ziehen: Kamera parallel verschieben",
    navZoomTip: "Taste [2] gedrückt halten + Maus ziehen: Stufenlos zoomen / Distanz",
    navSpinTip: "Taste [3] gedrückt halten + Maus ziehen: Freie 360° 3D-Kameradrehung",

    // Categories
    catPhysics: "Physik & Attraktoren",
    catPhysicsSec1: "Attraktoren",
    catPhysicsSec2: "Kräfte & Dynamik",

    catFormz: "FORMZ",
    catFormzSec1: "Kosmische Formen",
    catFormzSec2: "Deformer & Geometrie",

    catAudio: "Audio-Reaktiv & Licht",
    catAudioSec1: "Licht & Spektrum",
    catAudioSec2: "Beats & Wellen",

    catLive: "Live & 3D-Morph",
    catLiveSec1: "Live",
    catLiveSec2: "3D & Morph",

    catEssentials: "Essentials",
    catEssentialsSec1: "Ausgabe & Ansicht",
    catEssentialsSec2: "Master",
    catEssentialsSec3: "Presets & Reset",

    // Buttons
    btnAudioReact: "⚡ Audio ~",
    btnAttractorNeg: "Attraktor − (X)",
    btnAttractorPos: "Attraktor + (C)",
    btnGravity: "Gravitation",
    btnGravityWells: "Gravitationsfelder",
    btnChaosBoost: "Chaos-Boost",
    btnSlowMotion: "Zeitlupe",
    btnAntimatterCollision: "💫 Antimaterie-Annihilation",
    btnCryoLattice: "🧊 Cryo-Kristallgitter",
    btnHyperdriveWarp: "🛸 Wurmloch Hyperdrive",
    btnShockwave: "💥 Shockwave",
    btnShockwaveAudio: "⚡ Audio-Puls",
    tipShockwaveAudio: "Audio-Puls: Rhythmisches Skalieren & Leuchtwellen auf Bässe & Beats, ohne Partikel fortzuschleudern",
    btnAutoCam: "📹 AUTOCAM",
    btnExplosion: "Explosion",
    btnExplosionAudio: "AR (audio reactive)",
    tipExplosionAudio: "Audio-reaktive Explosion: reagiert auf echte Mic-Energie und Beat-Impulse, bleibt bei ruhigem Signal ruhig",
    btnRbBurst: "💥 Nova-Ringburst (Ö)",
    btnReset: "Reset",

    btnBlackHole: "🌀 Schwarzes Loch",
    btnNeutronStar: "⚡ Neutronenstern",
    btnSun: "☀️ SUN",
    btnGalaxy01: "🌌 Galaxy 01",
    btnGalaxy02: "🌌 Galaxy 02",
    btnGalaxy03: "🌌 Galaxy 03",
    btnGalaxy04: "🌌 Galaxy 04",
    btnRingNebula: "🪐 Ring-Nebel (M57)",
    btnSphere: "🪐 3D-Kugel",
    btnCrystal: "💎 Kristall-Gitter",
    btnBioCell: "🧬 Organische Bio-Zelle",
    btnAudioRing: "Audio-Ring",

    btnFireflyRiver: "🌊 Laminare Ströme",
    btnHexMatrix: "⬡ Waben-Matrix",
    btnFibonacciGrid: "🌻 Fibonacci-Spiralgitter",
    btnVortex: "Vortex",
    btnPortal: "Portal",
    btnTunnel: "Tunnel (J)",
    btnHelix: "Helix (D)",
    btnFractal: "Fraktal",
    btnGridPulse: "Grid-Puls",
    btnPattern: "Muster",
    btnDistortion: "Distortion",
    btnSwarm: "Schwarm",
    btnStreams: "Streams",

    btnBioluminescence: "Biolumineszenz 1 (Q)",
    btnBioSynapse: "✨ Synaptische Bio",
    sliderBioSynapse: "Synapsen-Power",
    btnElectro: "⚡ Electro (E)",
    btnSpectrumColor: "Spektrum-Farben (W)",
    btnChromaWave: "Chroma-Welle (T)",
    btnBeatFlash: "Beat-Flash (Z)",
    btnShimmer: "Shimmer",
    btnStrobeField: "Strobe-Feld (F)",
    btnStarSky: "Sternhimmel",
    btnColorInvert: "Farb-Invert",

    btnBeatPulse: "Beat-Puls (O)",
    btnScaleFreq: "Scale/Freq.",
    btnAudioSize: "Audio-Größe (U)",
    btnBreath: "Atem (H)",
    btnSpectralCathedral: "⛪ Spektral-Kathedrale",
    btnPulseBloom: "🌺 Pulse Bloom",
    btnShapeChange: "Shape-Change",
    btnBandExplode: "Band-Explosion",
    btnComplex: "Complex",
    btnLiquidRipple: "Liquid-Ripple",
    btnRipple: "Ripple",
    btnCaustics: "Caustics",
    btnInkBleed: "Ink-Bleed",

    btnPip: "📺 Syphon Popup",
    tipPip: "Öffnet ein schwebendes Vorschaufenster (Picture-in-Picture) für Syphon/OBS/Resolume",
    btnFullscreen: "⛶ Vollbild (F)",
    btnFindSwarm: "🎯 Swarm finden",
    btnCameraFollow: "🎥 Schwarm folgen",
    tipCameraFollow: "Zentriert die Kamera automatisch auf den fließenden Schwerpunkt des Schwarms",
    btnViewportTop: "📐 Top Viewport",
    tipViewportTop: "Beschränkt die Animation auf den Bereich oberhalb der Steuerungs-Leiste (100% verdeckungsfrei)",
    btnMuteRender: "⏸ Render Mute",
    tipMuteRender: "Pausiert das lokale Canvas-Rendern zur GPU/CPU-Schonung (ideal bei aktivem Ausgabefenster)",
    btnTapBpm: "⏱ Tap BPM",
    btnBlackout: "⚡ Blackout (B)",
    btnExportPreset: "💾 Preset Export",
    btnImportPreset: "📂 Preset Import",
    btnSpin360: "🔄 360 Spin",
    btnAutoMix: "Auto-Mix (A)",
    btnWrapMode: "Wrap-Modus (Ä)",
    btnLiveMode: "Live/Resolume (S)",

    btnStarcodeMorph: "STARCODE-Morph",
    btnBrandMorph: "✦ STUDIOPESC.COM",
    btnObjMorph: "3D-Modell Morph",
    btnObjDef1: "✦ Voxel-Lift",
    btnObjDef2: "⚡ Spikes",
    btnObjDef3: "〰 Harmonics",
    customWordPlaceholder: "Eigener Text (max. 10)...",
    btnCustomMorph: "✦ Text Morphen",
    btnCustomMorphOff: "✕ Text Aus",
    btnLoadObj: "📂 OBJ laden",
    btnBluePerm: "Blau-Permanent",
    btnRedPerm: "Rot-Permanent",
    btnBwPerm: "Schwarz/Weiß (B&W)",

    // Sliders
    sliderMaster: "Master",
    sliderSize: "Größe",
    sliderSpeed: "Tempo",
    sliderRotation: "Rotation",
    sliderBloom: "Bloom",
    sliderAudioGain: "Audio-Gain",
    sliderBioIntensity: "Bio-Licht",
    sliderFocusDistance: "Fokus-Distanz",
    sliderAutoCamDist: "AutoCam-Fahrstrecke",
    sliderAutoCamSpeed: "AutoCam-Tempo",
    sliderDofBlur: "Bokeh / DOF",
    sliderAutoMixTempo: "Auto-Mix-Tempo",
    sliderHue: "Farbton",

    btnFarBokeh: "Weit-Bokeh",
    btnNearBokeh: "Nah-Bokeh ✦",
    btnAF: "AF",
    btnAFActive: "AF ✦",
    btnAudioRot: "⚡ Audio",
    btnAudioBokeh: "⚡ Audio",
    btnAudioBokehActive: "⚡ Audio ✦",
    colorOff: "Aus",

    // Toolboxes
    boxControls: "Regler",
    boxBokeh: "Optik & Bokeh (DOF)",
    boxFormat: "Format",
    boxPresets: "Presets",
    boxMidi: "MIDI-Learn",

    optWidth: "B:",
    optHeight: "H:",
    optParticles: "Partikel:",

    presetSave: "💾 Speichern",
    presetDelete: "🗑 Löschen",
    presetRandom: "🎲 Random",
    presetNext: "⏭ Next",
    presetGroupFactory: "Werkseinstellungen",
    presetGroupUser: "Eigene Presets",
    presetPromptName: "Name für das neue Preset:",
    presetAlertFactory: "Werkseinstellungen können nicht gelöscht werden.",

    // Tooltips
    tipCollapse: "Klicken zum Ein-/Ausklappen",
    tipRotAudio: "Audio-Reaktivität für Rotation (Kamera dreht dynamisch im Rhythmus des Basses)",
    tipAutofocus: "Autofokus aktivieren / deaktivieren (folgt der Kamera-Distanz dynamisch)",
    tipDofInvert: "Umschalten: Weit unscharf (Hintergrund) ⇄ Nah unscharf (Vordergrund)",
    tipBokehAudio: "Audio-Reaktivität für Bokeh (Unschärfe und Bokeh-Scheiben pulsieren dynamisch mit dem Beat)",
    tipColorScrub: "Klicken & Ziehen zum Einstellen des globalen Farbtons",
    tipColorReset: "Farbton ausschalten / zurücksetzen",
  },
  en: {
    langLabel: "EN",
    startTitle: "STARCODE",
    startSubtitle: "GPU Particle Engine",
    visitorLabel: "Visits",
    startBuildDate: "RELEASE :: 16.08.2026",
    startButton: "Start (Enable Audio & Motion)",
    startMicHint: "Allow microphone access for audio reactivity (optional).",
    startShowHint: "For live shows: use Syphon Popup to open the clean output frame (Document Picture-in-Picture) without browser URL bar.",
    creatorLink: "studiopesc.com",
    aboutButton: "About & Contact",
    hudAboutBtn: "ℹ Info",
    aboutModalTitle: "About STARCODE & STUDIO PESC",
    tabAbout: "About Project",
    tabGuide: "Tutorial & Resolume VJ",
    tabContact: "Creator & Contact",
    aboutText1: "STARCODE is an ultra-high performance GPU-accelerated 3D particle system and live visual synthesizer, engineered for live audio-visual concerts, VJ performances, theater stages, and immersive installations.",
    aboutText2: "All particle physics, fluid flows, gravitational fields, and astrophysics simulations (Black Hole, Neutron Star, Sun, Galaxies) are computed in real time on the GPU (WebGL2 GPGPU shaders) at 120 FPS.",
    aboutFeature1Title: "Realtime Audio-Reactivity",
    aboutFeature1Desc: "5-band FFT frequency spectrum, beat detection & transient flux analysis driving physical forces, colors, and geometries.",
    aboutFeature2Title: "Live VJ & Resolume Syphon Workflow",
    aboutFeature2Desc: "1-click frameless floating output window, BroadcastChannel state sync, and continuous background rendering without freeze.",
    aboutFeature3Title: "Cinematic Optics",
    aboutFeature3Desc: "Autofocus tracking, bidirectional Bokeh / Depth of Field, near/far blur inversion, and HDR bloom.",

    // Tutorial & Guide Content (EN)
    guideNavTitle: "1. Quick Start & 3D Camera Controls",
    guideNavDesc: "Navigate the 3D camera freely in space at any time:",
    guideNavOrbit: "Standard Orbit:",
    guideNavOrbitDesc: "Left-click and drag to orbit 360° around the center.",
    guideNavPan: "Camera Pan [1]:",
    guideNavPanDesc: "Hold key <code>1</code> + drag mouse to pan camera in screen space (X/Y plane).",
    guideNavZoom: "Smooth Zoom [2]:",
    guideNavZoomDesc: "Hold key <code>2</code> + drag mouse up/down, or scroll mouse wheel/trackpad.",
    guideNavSpin: "Unrestricted 3D Spin [3]:",
    guideNavSpinDesc: "Hold key <code>3</code> + drag mouse to tumble camera in full 3D space with no polar lock.",
    guideNavReset: "Find Swarm / Reset:",
    guideNavResetDesc: "Click <code>🎯 Find Swarm</code> or press <code>R</code> to instantly reset camera and particle dynamics.",

    guideVJTitle: "2. Live VJ & Resolume Arena / Syphon / OBS Workflow",
    guideVJDesc: "STARCODE is engineered for high-reliability live stage performance using a dual-window director architecture:",
    guideVJStep1Title: "Step 1: Dual-Window Setup (Director & Output)",
    guideVJStep1Desc: "Open the frameless output window by clicking <code>⧉ Output Window (Syphon)</code> or appending <code>?role=output</code> to the URL. This window renders a clean, frameless feed with no UI and hidden cursor. Keep your main window (or <code>?role=control</code>) on your control monitor to adjust buttons and sliders. Both windows stay synced with zero latency via <code>BroadcastChannel</code>.",
    guideVJStep2Title: "Step 2: Connect to Resolume Arena / OBS",
    guideVJStep2Desc: "In Resolume Arena, navigate to <b>Sources → Screen Capture</b> (or Syphon / NDI) and select the <b>'STARCODE — Output'</b> window. STARCODE includes an automatic <b>MediaStream Keepalive Pipeline</b>, ensuring the WebGL engine continues rendering at full 60–120 FPS in the background without freezing when Resolume is in focus.",
    guideVJStep3Title: "Step 3: Live Audio Routing",
    guideVJStep3Desc: "Set your audio input to your DJ mixer, audio interface, or virtual audio cable (e.g. BlackHole or Loopback). The 5-band FFT spectrum analyzer and transient flux detector drive particle forces, shockwaves, and flashes live with the music.",

    guideFormzTitle: "3. Cosmic Forms (FORMZ) & 3D Morphing",
    guideFormzDesc: "Transform the swarm into monumental astrophysical celestial structures in real time:",
    guideBlackHoleTitle: "🌀 Black Hole (Gargantua):",
    guideBlackHoleDesc: "Massive gravitational event horizon with swirling relativistic accretion disk.",
    guideNeutronTitle: "⚡ Neutron Star (Pulsar):",
    guideNeutronDesc: "Hyper-dense rotating magnetar core with high-energy relativistic polar jets.",
    guideSunTitle: "☀️ SUN:",
    guideSunDesc: "Glowing thermonuclear plasma core with dynamic solar corona and coronal mass ejections.",
    guideMorphTitle: "3D Morphing & Custom OBJ Models:",
    guideMorphDesc: "Click <code>STARCODE Morph</code> (Cmd/Ctrl+Shift+M) to assemble the entire swarm into a genuine extruded 3D wordmark. Click <code>📂 Load OBJ</code> to load any custom 3D model (.obj) directly from your computer.",

    guideOpticsTitle: "4. Cinematic Optics, Hue Wheel, Presets & MIDI",
    guideOpticsDesc: "<b>Autofocus & Bokeh:</b> <code>AF</code> dynamically tracks camera-to-subject distance. Adjust blur amount and toggle <code>Far-Bokeh</code> ⇄ <code>Near-Bokeh</code> for depth of field.<br><b>Color Scrubbing:</b> Drag on the <code>Hue</code> slider for full 360° color sweeps.<br><b>MIDI Learn:</b> Click any target in the <code>MIDI Learn</code> section, then move a fader or knob on your hardware MIDI controller.<br><b>Presets:</b> Save your favorite settings permanently with <code>💾 Save</code> or select from curated factory presets.",

    creatorTitle: "Creator & Concept",
    creatorName: "Peter Schönhardt",
    creatorRole: "Designer, Creative Technologist & Visual Artist",
    creatorCompany: "STUDIO PESC",
    creatorWeb: "studiopesc.com",
    creatorEmail: "peter@studiopesc.com",
    creatorLocation: "Vienna / Austria & Worldwide",
    creatorBio: "Specialized in generative art, brand identity, interactive 3D experiences, motion design, and immersive live visual performance.",
    closeModal: "Close",

    // HUD Top
    fps: "fps",
    particles: "Particles",
    audioMic: "Audio: Microphone",
    audioSynth: "Audio: Synthetic",
    regieWindow: "Control Window",
    midiConnected: "MIDI: Connected",
    autoMixPrefix: "Auto-Mix: ",
    liveBadge: "● LIVE",
    resizeGripTitle: "Drag up/down to adjust panel height or double click to toggle",
    exitRegieBtn: "‹ Standard Mode (Show Visuals & Controls here)",
    exitRegieTitle: "Click to leave control mode and display the live particle simulation inside this window",

    // Navigation helper
    navTitle: "Camera:",
    navPan: "<kbd>1</kbd> ✥ Pan",
    navZoom: "<kbd>2</kbd> ⇕ Zoom",
    navSpin: "<kbd>3</kbd> 🔄 3D-Spin",
    navPanTip: "Hold key [1] + drag mouse: Pan camera plane",
    navZoomTip: "Hold key [2] + drag mouse: Smooth zoom distance",
    navSpinTip: "Hold key [3] + drag mouse: Free 360° 3D orbital spin",

    // Categories
    catPhysics: "Physics & Attractors",
    catPhysicsSec1: "Attractors",
    catPhysicsSec2: "Forces & Dynamics",

    catFormz: "FORMZ",
    catFormzSec1: "Cosmic Forms",
    catFormzSec2: "Deformers & Geometry",

    catAudio: "Audio-Reactive & Light",
    catAudioSec1: "Light & Spectrum",
    catAudioSec2: "Beats & Waves",

    catLive: "Live & 3D Morph",
    catLiveSec1: "Live",
    catLiveSec2: "3D & Morph",

    catEssentials: "Essentials",
    catEssentialsSec1: "Output & View",
    catEssentialsSec2: "Master",
    catEssentialsSec3: "Presets & Reset",

    // Buttons
    btnAudioReact: "⚡ Audio ~",
    btnAttractorNeg: "Attractor − (X)",
    btnAttractorPos: "Attractor + (C)",
    btnGravity: "Gravity",
    btnGravityWells: "Gravity Wells",
    btnChaosBoost: "Chaos Boost",
    btnSlowMotion: "Slow Motion",
    btnAntimatterCollision: "💫 Antimatter Collision",
    btnCryoLattice: "🧊 Cryo Lattice",
    btnHyperdriveWarp: "🛸 Hyperdrive Warp",
    btnShockwave: "💥 Shockwave",
    btnShockwaveAudio: "⚡ Audio Pulse",
    tipShockwaveAudio: "Audio Pulse: Rhythmic scaling & glowing waves on beats without blasting particles away",
    btnAutoCam: "📹 AUTOCAM",
    btnExplosion: "Explosion",
    btnExplosionAudio: "AR (audio reactive)",
    tipExplosionAudio: "Audio-reactive explosion: follows real mic energy and beat onsets, stays calm on quiet input",
    btnRbBurst: "💥 Nova Ringburst (Ö)",
    btnReset: "Reset",

    btnBlackHole: "🌀 Black Hole",
    btnNeutronStar: "⚡ Neutron Star",
    btnSun: "☀️ SUN",
    btnGalaxy01: "🌌 Galaxy 01",
    btnGalaxy02: "🌌 Galaxy 02",
    btnGalaxy03: "🌌 Galaxy 03",
    btnGalaxy04: "🌌 Galaxy 04",
    btnRingNebula: "🪐 Ring Nebula (M57)",
    btnSphere: "🪐 3D Sphere",
    btnCrystal: "💎 Crystal Lattice",
    btnBioCell: "🧬 Organic Bio-Cell",
    btnAudioRing: "Audio Ring",

    btnFireflyRiver: "🌊 Laminar Streams",
    btnHexMatrix: "⬡ Honeycomb Matrix",
    btnFibonacciGrid: "🌻 Fibonacci Lattice",
    btnVortex: "Vortex",
    btnPortal: "Portal",
    btnTunnel: "Tunnel (J)",
    btnHelix: "Helix (D)",
    btnFractal: "Fractal",
    btnGridPulse: "Grid Pulse",
    btnPattern: "Pattern",
    btnDistortion: "Distortion",
    btnSwarm: "Swarm",
    btnStreams: "Streams",

    btnBioluminescence: "Bioluminescence 1 (Q)",
    btnBioSynapse: "✨ Synaptic Bio",
    sliderBioSynapse: "Synapse Flash",
    btnElectro: "⚡ Electro (E)",
    btnSpectrumColor: "Spectrum Colors (W)",
    btnChromaWave: "Chroma Wave (T)",
    btnBeatFlash: "Beat Flash (Z)",
    btnShimmer: "Shimmer",
    btnStrobeField: "Strobe Field (F)",
    btnStarSky: "Star Sky",
    btnColorInvert: "Invert Color",

    btnBeatPulse: "Beat Pulse (O)",
    btnScaleFreq: "Scale/Freq.",
    btnAudioSize: "Audio Size (U)",
    btnBreath: "Breath (H)",
    btnSpectralCathedral: "⛪ Spectral Cathedral",
    btnPulseBloom: "🌺 Pulse Bloom",
    btnShapeChange: "Shape Change",
    btnBandExplode: "Band Explosion",
    btnComplex: "Complex",
    btnLiquidRipple: "Liquid Ripple",
    btnRipple: "Ripple",
    btnCaustics: "Caustics",
    btnInkBleed: "Ink Bleed",

    btnPip: "📺 Syphon Popup",
    tipPip: "Opens a floating preview window (Picture-in-Picture) for Syphon/OBS/Resolume",
    btnFullscreen: "⛶ Fullscreen (F)",
    btnFindSwarm: "🎯 Find Swarm",
    btnCameraFollow: "🎥 Follow Swarm",
    tipCameraFollow: "Automatically centers camera on the flowing center of mass",
    btnViewportTop: "📐 Top Viewport",
    tipViewportTop: "Constrains canvas strictly to the visible area above the UI control bar (100% unobscured)",
    btnMuteRender: "⏸ Mute Local Render",
    tipMuteRender: "Pauses local canvas rendering to save GPU/CPU power (ideal when output window is active)",
    btnTapBpm: "⏱ Tap BPM",
    btnBlackout: "⚡ Blackout (B)",
    btnExportPreset: "💾 Export Preset",
    btnImportPreset: "📂 Import Preset",
    btnSpin360: "🔄 360 Spin",
    btnAutoMix: "Auto-Mix (A)",
    btnWrapMode: "Wrap Mode (Ä)",
    btnLiveMode: "Live/Resolume (S)",

    btnStarcodeMorph: "STARCODE Morph",
    btnBrandMorph: "✦ STUDIOPESC.COM",
    btnObjMorph: "3D Model Morph",
    btnObjDef1: "✦ Voxel Lift",
    btnObjDef2: "⚡ Spikes",
    btnObjDef3: "〰 Harmonics",
    customWordPlaceholder: "Custom Text (max. 10)...",
    btnCustomMorph: "✦ Morph Text",
    btnCustomMorphOff: "✕ Text Off",
    btnLoadObj: "📂 Load OBJ",
    btnBluePerm: "Permanent Blue",
    btnRedPerm: "Permanent Red",
    btnBwPerm: "Black & White (B&W)",

    // Sliders
    sliderMaster: "Master",
    sliderSize: "Size",
    sliderSpeed: "Speed",
    sliderRotation: "Rotation",
    sliderBloom: "Bloom",
    sliderAudioGain: "Audio Gain",
    sliderBioIntensity: "Bio Light",
    sliderFocusDistance: "Focus Distance",
    sliderAutoCamDist: "AutoCam Travel Distance",
    sliderAutoCamSpeed: "AutoCam Speed",
    sliderDofBlur: "Bokeh / DOF",
    sliderAutoMixTempo: "Auto-Mix Speed",
    sliderHue: "Hue",

    btnFarBokeh: "Far-Bokeh",
    btnNearBokeh: "Near-Bokeh ✦",
    btnAF: "AF",
    btnAFActive: "AF ✦",
    btnAudioRot: "⚡ Audio",
    btnAudioBokeh: "⚡ Audio",
    btnAudioBokehActive: "⚡ Audio ✦",
    colorOff: "Off",

    // Toolboxes
    boxControls: "Controls",
    boxBokeh: "Optics & Bokeh (DOF)",
    boxFormat: "Format",
    boxPresets: "Presets",
    boxMidi: "MIDI Learn",

    optWidth: "W:",
    optHeight: "H:",
    optParticles: "Particles:",

    presetSave: "💾 Save",
    presetDelete: "🗑 Delete",
    presetRandom: "🎲 Random",
    presetNext: "⏭ Next",
    presetGroupFactory: "Factory Presets",
    presetGroupUser: "User Presets",
    presetPromptName: "Name for the new preset:",
    presetAlertFactory: "Factory presets cannot be deleted.",

    // Tooltips
    tipCollapse: "Click to expand / collapse",
    tipRotAudio: "Audio reactivity for rotation (camera accelerates on beat & bass)",
    tipAutofocus: "Toggle autofocus (dynamically tracks camera-to-subject distance)",
    tipDofInvert: "Switch: Far blur (background) ⇄ Near blur (foreground)",
    tipBokehAudio: "Audio reactivity for bokeh (blur & bokeh discs pulse with the beat)",
    tipColorScrub: "Click & drag to set global hue",
    tipColorReset: "Turn off / reset hue",
  },
};

let currentLang = "en";

export function applyDOMTranslations(root = document) {
  const lang = getLang();
  // Elements with data-i18n
  root.querySelectorAll("[data-i18n]").forEach((el) => {
    const key = el.dataset.i18n;
    if (key) {
      const val = t(key);
      if (val.includes("<") && val.includes(">")) {
        el.innerHTML = val;
      } else {
        el.textContent = val;
      }
    }
  });

  // Elements with data-i18n-title
  root.querySelectorAll("[data-i18n-title]").forEach((el) => {
    const key = el.dataset.i18nTitle;
    if (key) el.title = t(key);
  });

  // Sync any language switch buttons in root
  root.querySelectorAll(".langBtn").forEach((btn) => {
    btn.classList.toggle("active", btn.dataset.lang === lang);
  });
}

export function initI18n() {
  if (typeof window === "undefined") return "en";
  const urlParam = new URLSearchParams(window.location.search).get("lang");
  if (urlParam && (urlParam === "de" || urlParam === "en")) {
    currentLang = urlParam;
  } else {
    // Default: Always start in English
    currentLang = "en";
  }
  localStorage.setItem("starcode_lang", currentLang);
  document.documentElement.lang = currentLang;

  // Apply translations to DOM
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => applyDOMTranslations());
  } else {
    applyDOMTranslations();
  }

  window.addEventListener("starcode:langchange", () => {
    applyDOMTranslations();
  });

  return currentLang;
}

export function getLang() {
  return currentLang;
}

export function setLang(lang) {
  if (lang !== "de" && lang !== "en") return;
  currentLang = lang;
  localStorage.setItem("starcode_lang", lang);
  document.documentElement.lang = lang;
  window.dispatchEvent(new CustomEvent("starcode:langchange", { detail: { lang } }));
}

export function toggleLang() {
  setLang(currentLang === "de" ? "en" : "de");
  return currentLang;
}

export function t(key) {
  return translations[currentLang]?.[key] || translations.en[key] || translations.de[key] || key;
}
